import { useEffect, useState, type ReactNode } from "react";
import { api, type Room, type Task, type User } from "../api";
import { upsertTask, useStore } from "../store";

export const useMe = () => useStore((s) => s.me!);
export const isOwner = (u: User | null) => u?.role === "owner";

/** A person: their photo, or their initial in their colour; optionally the online dot. */
export function Avatar({ user, size = 18, dot = false }: { user?: User; size?: number; dot?: boolean }) {
  const presence = useStore((s) => (dot && user ? s.presence[user.id] : undefined));
  const [broken, setBroken] = useState(false);
  const photo = user?.avatarUrl && !broken ? user.avatarUrl : null;
  return (
    <span className="av" style={{ ["--s" as any]: `${size}px`, background: photo ? undefined : user?.color ?? "#555" }}>
      {photo ? <img src={photo} alt="" referrerPolicy="no-referrer" onError={() => setBroken(true)} /> : (user?.name ?? "?")[0]}
      {dot && <span className={`pres${presence === "active" ? " on" : ""}`} title={presence === "active" ? "Active" : presence === "idle" ? "Away" : "Offline"} />}
    </span>
  );
}
export const Ini = ({ user }: { user?: User; lg?: boolean }) => <Avatar user={user} size={18} />;

export function roomTitle(r: Room | undefined, meId: number, users: Record<number, User>) {
  if (!r) return "";
  if (r.kind === "group") return `#${r.name}`;
  const others = r.members.filter((u) => u !== meId);
  return others.length ? others.map((u) => users[u]?.name ?? "Someone").join(", ") : users[meId]?.name ?? "You";
}
export function roomColor(r: Room | undefined, meId: number, users: Record<number, User>) {
  if (!r) return "#8a94a6";
  if (r.kind === "group") return r.color ?? "#8a94a6";
  return users[r.members.find((u) => u !== meId) ?? meId]?.color ?? "#8a94a6";
}
export const dmPartner = (r: Room | undefined, meId: number) => (r?.kind === "dm" && r.members.length === 2 ? r.members.find((u) => u !== meId) ?? null : null);

export function Check({ task, size }: { task: Task; size?: number }) {
  const me = useMe();
  const can = isOwner(me) || task.ownerId === me.id;
  const cls = task.status === "done" ? "ck on" : task.status === "doing" ? "ck half" : "ck";
  return (
    <button className={cls} disabled={!can} style={size ? { width: size, height: size } : undefined}
      title={task.status === "done" ? "Mark open" : "Mark done"}
      onClick={(e) => { e.stopPropagation(); if (can) setStatus(task, task.status === "done" ? "open" : "done"); }}>
      {task.status === "done" ? "✓" : ""}
    </button>
  );
}

export async function setStatus(task: Task, status: Task["status"]) {
  const prev = task;
  upsertTask({ ...task, status, doneAt: status === "done" ? new Date().toISOString() : null });
  try { upsertTask(await api.editTask(task.id, { status })); }
  catch (e: any) { upsertTask(prev); toast(e.message || "Couldn't change it"); }
}

// ---------- toast ----------
let push: ((s: string) => void) | null = null;
export const toast = (s: string) => push?.(s);
export function Toaster() {
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => {
    let t: number | undefined;
    push = (s) => { setMsg(s); clearTimeout(t); t = window.setTimeout(() => setMsg(null), 3200); };
    return () => { push = null; };
  }, []);
  return <div className={`toast${msg ? " show" : ""}`}>{msg}</div>;
}

export function Veil({ children, onClose }: { children: ReactNode; onClose: () => void }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    addEventListener("keydown", k);
    return () => removeEventListener("keydown", k);
  }, [onClose]);
  return <div className="veil" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>{children}</div>;
}

export const sizeLabel = (n: number) => (n < 1024 ? `${n} B` : n < 1048576 ? `${Math.round(n / 1024)} KB` : `${(n / 1048576).toFixed(1)} MB`);
